import { navigate } from '@client/routes';

type ViewTransition = {
  finished: Promise<void>;
  ready: Promise<void>;
  updateCallbackDone: Promise<void>;
  skipTransition: () => void;
};

type ViewTransitionDocument = Document & {
  startViewTransition?: (
    callback: () => void | Promise<void>,
  ) => ViewTransition;
};

type TransitionLinkClickEvent = Pick<
  MouseEvent,
  'defaultPrevented' | 'button' | 'metaKey' | 'altKey' | 'ctrlKey' | 'shiftKey'
>;

const AUTH_ROUTE_TRANSITION_CLASS = 'auth-route-transitioning';

const isReducedMotionPreferred = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const getViewTransitionDocument = (): ViewTransitionDocument | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const viewTransitionDocument = document as ViewTransitionDocument;
  if (typeof viewTransitionDocument.startViewTransition !== 'function') {
    return null;
  }

  return viewTransitionDocument;
};

export const navigateWithAuthTransition = (path: string): void => {
  const viewTransitionDocument = getViewTransitionDocument();

  if (!viewTransitionDocument || isReducedMotionPreferred()) {
    navigate(path);
    return;
  }

  const rootElement = viewTransitionDocument.documentElement;
  const clearTransitionClass = (): void => {
    rootElement.classList.remove(AUTH_ROUTE_TRANSITION_CLASS);
  };

  rootElement.classList.add(AUTH_ROUTE_TRANSITION_CLASS);

  try {
    const transition = viewTransitionDocument.startViewTransition?.(() => {
      navigate(path);
    });

    if (!transition) {
      clearTransitionClass();
      return;
    }

    // Clear the helper class once DOM update is committed.
    // Keeping it until `finished` can trigger a second entry animation
    // right after the view transition ends, which looks like a full-page flash.
    void transition.updateCallbackDone.finally(clearTransitionClass);
  } catch {
    clearTransitionClass();
    navigate(path);
  }
};

export const shouldHandleTransitionLinkClick = (
  event: TransitionLinkClickEvent,
): boolean => {
  if (event.defaultPrevented) {
    return false;
  }

  if (event.button !== 0) {
    return false;
  }

  if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
    return false;
  }

  return true;
};
